import { useCallback, useEffect, useMemo, useRef } from "react";
import { getFromLocalStorage, saveToLocalStorage } from "src/store";
import { ChatConfig, User } from "src/types";

declare global {
  interface Window {
    fcWidget: any;
  }
}

const RESTORE_KEY = "restore";

export function useChat(
  isAuth: boolean,
  user: User | null,
  config: ChatConfig | null
) {
  const w = window as Window & typeof globalThis;

  const userRestoreString = getFromLocalStorage(RESTORE_KEY);
  const userRestoreData = JSON.parse(userRestoreString || "{}");
  const userData = useMemo(() => ({ ...user, id: undefined }), [user]);
  const backgroundRef = useRef('');

  const userRestoreKey = useMemo(() => user?.id.slice(-6) || "", [user]);
  const saveChatRestoreId = useCallback(
    (id: string) => {
      // TODO: we should store this id on database
      if (user?.id) {
        saveToLocalStorage(
          RESTORE_KEY,
          JSON.stringify({
            ...userRestoreData,
            [userRestoreKey]: id,
          })
        );
      }
    },
    [userRestoreData, user, userRestoreKey]
  );

  const initFreshChat = useCallback(async () => {
    try {
      if (window.fcWidget?.isInitialized()) return;
      if (config) {
        const { freshchat_token, freshchat_host } = config;

        w.fcWidget?.init({
          token: freshchat_token,
          host: freshchat_host,
          ...userData,
          restoreId: userRestoreData[userRestoreKey || ""] || user?.id,
          externalId: user?.id,
        });

        w.fcWidget?.on('widget:closed', () => {
          window.document.body.style.backgroundColor = backgroundRef.current;

          const metaTag : HTMLMetaElement | null = document.querySelector('meta[name=theme-color]');
          if (!metaTag) return;
          metaTag.content = backgroundRef.current;
        });

        w.fcWidget?.on('widget:opened', () => {
          const widgetColor = '#f2af05';

          backgroundRef.current = window.document.body.style.backgroundColor;
         
          const metaTag : HTMLMetaElement | null = document.querySelector('meta[name=theme-color]');
          if (!metaTag) return;
          metaTag.content = widgetColor;
        });

        w.fcWidget?.user.get(function (resp: any) {
          const getUserStatus = resp && resp.status,
            data = resp && resp.data;

          // If status different from 200,the user is not created, create a new user
          if (getUserStatus !== 200) {
            w.fcWidget?.user.setProperties(userData);
            w.fcWidget?.on("user:created", function (resp: any) {
              const userCreatedStatus = resp && resp.status,
                data = resp && resp.data;
              if (userCreatedStatus === 200) {
                if (data.restoreId) {
                  saveChatRestoreId(data.restoreId as string);
                }
              }
            });
          } else {
            saveChatRestoreId(data.restoreId as string);
          }
        });
      } else {
        throw new Error("No Freshdesk configuration found");
      }
    } catch (error) {
      throw error;
    }
  }, [
    config,
    userData,
    user,
    w.fcWidget,
    saveChatRestoreId,
    userRestoreData,
    userRestoreKey,
  ]);

  const initialize = useCallback(
    (document: Document, scriptId: string) => {
      var e;
      if (document.getElementById(scriptId)) {
        initFreshChat();
      } else {
        e = document.createElement("script");
        e.id = scriptId;
        e.async = !0;
        e.src = "https://bighealth.freshchat.com/js/widget.js";
        e.onload = initFreshChat;
        document.head.appendChild(e);
      }
    },
    [initFreshChat]
  );

  const initChat = useCallback(() => {
    initialize(document, "Freshchat-js-sdk");
  }, [initialize]);

  const destroyChat = useCallback(() => {
    if (!w.fcWidget?.isInitialized()) return;
    w.fcWidget?.destroy();
  }, [w.fcWidget]);

  const openChat = useCallback(() => {
    if (!w.fcWidget?.isOpen()) {
      w.fcWidget?.open();
      w.fcWidget?.user.clear();
    }
  }, [w.fcWidget]);

  useEffect(() => {
    if (isAuth && user && config) {
      initChat();
    } else {
      destroyChat();
    }
  }, [isAuth, user, config, initChat, destroyChat]);

  return { initChat, destroyChat, openChat };
}
